import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useDebounced } from '@/api/useDebounced';
import { useApi } from '@/api/useApi';
import { ApiError } from '@/api/client';
import type { CreateInboundResult, InboundWarehouses, ManualOrderSku } from '@/api/types';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PermissionDenied } from '@/components/RequirePermission';
import { SkuMeta } from '@/components/SkuMeta';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage, type TranslateFn } from '@/i18n/LanguageContext';
import { newIdempotencyKey } from '@/pages/Orders/Create';

/**
 * Ops create-supply form. Seller -> warehouse -> items (searched, never a pasted id) -> optional reference / expected date ->
 * create. POST /api/v1/office/inbounds reuses the seller-request model and rules (EXPECTED, the seller's own SKUs, a warehouse
 * that serves the seller, a number issued by the server); this screen only checks what it can see (whole qty > 0, no duplicate
 * lines) and renders the server's answer for everything else. The created document is the seller's own supply: it shows in their
 * portal as announced by the warehouse.
 */

type Line = { readonly skuId: string; readonly code: string; readonly name: string; qty: string };

const WHOLE_UNITS = /^[1-9]\d*$/;

export function lineProblem(t: TranslateFn, line: Line): string | null {
  return WHOLE_UNITS.test(line.qty.trim()) ? null : t('inboundCreate.qtyInvalid', { sku: line.code });
}

/** The server's domain errors as a sentence naming the item by its CODE; null for anything generic (the standard banner renders it). */
export function describeInboundError(t: TranslateFn, err: unknown, lines: readonly Line[]): string | null {
  if (!(err instanceof ApiError)) return null;
  const p = err.params as Record<string, unknown>;
  const sku = (): string => lines.find((l) => l.skuId === p.skuId)?.code ?? String(p.skuCode ?? '');
  switch (err.code) {
    case 'SELLER_REQUEST.OWNER_NOT_ALLOWED':
    case 'SELLER_REQUEST.OWNER_REQUIRED':
      return t('inboundCreate.err.ownerNotAllowed');
    case 'SELLER_REQUEST.WAREHOUSE_NOT_ALLOWED':
    case 'SELLER_REQUEST.NO_WAREHOUSE':
      return t('inboundCreate.err.warehouseNotAllowed');
    case 'SELLER_REQUEST.CONTRACT_NOT_ACTIVE':
      return t('inboundCreate.err.contractNotActive');
    case 'ORDER.SKU_NOT_OWNED_BY_SELLER':
      return t('inboundCreate.err.skuNotOwned', { sku: sku() });
    case 'ORDER.LINES_REQUIRED':
      return t('inboundCreate.err.linesRequired');
    case 'SELLER_REQUEST.REFERENCE_INVALID':
      return t('inboundCreate.err.referenceInvalid');
    case 'SELLER_REQUEST.NUMBER_TAKEN':
      return t('inboundCreate.err.numberTaken');
    case 'COMMON.NOT_FOUND':
      return t('inboundCreate.err.notFound');
    default:
      return null;
  }
}

export function InboundCreatePage(): JSX.Element {
  const { hasPermission } = useAuth();
  const { t } = useLanguage();
  if (!hasPermission('RECEIVING.WRITE')) return <PermissionDenied reason={t('inboundCreate.permissionDenied')} />;
  return <InboundCreateForm />;
}

function InboundCreateForm(): JSX.Element {
  const { t } = useLanguage();
  const options = useApi(() => endpoints.inboundCreateOptions(), []);

  const [ownerId, setOwnerId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<CreateInboundResult | null>(null);

  // Only the warehouses that serve THIS seller through an active contract (and are inside the caller's scope) are offered.
  const warehouses = useApi(
    () => (ownerId ? endpoints.inboundCreateWarehouses(ownerId) : Promise.resolve<InboundWarehouses>({ warehouses: [], reason: 'READY' })),
    [ownerId],
  );
  const offered = warehouses.status === 'success' ? warehouses.data.warehouses : [];

  // One serving warehouse (the common case) is chosen for the operator; a choice that is no longer offered is dropped.
  useEffect(() => {
    if (warehouses.status !== 'success' || !ownerId) return;
    const ids = warehouses.data.warehouses.map((w) => w.id);
    if (warehouseId && !ids.includes(warehouseId)) setWarehouseId('');
    else if (!warehouseId && ids.length === 1) setWarehouseId(ids[0]!);
  }, [warehouses.status, warehouses.data, ownerId, warehouseId]);

  const q = useDebounced(search.trim(), 250);
  const ready = ownerId !== '' && warehouseId !== '';
  const skus = useApi(
    () => (ownerId ? endpoints.inboundCreateSkus({ ownerId, q: q || undefined, limit: 20 }) : Promise.resolve({ rows: [] as ManualOrderSku[] })),
    [ownerId, q],
  );

  const problems = useMemo(() => new Map(lines.map((l) => [l.skuId, lineProblem(t, l)])), [lines, t]);
  const hasProblems = [...problems.values()].some((p) => p !== null);

  function addLine(sku: ManualOrderSku): void {
    setError(null);
    const existing = lines.find((l) => l.skuId === sku.sku_id);
    if (existing) {
      // Never a second line for the same SKU - bump the existing one instead.
      const current = WHOLE_UNITS.test(existing.qty.trim()) ? Number(existing.qty.trim()) : 0;
      setLines((prev) => prev.map((l) => (l.skuId === sku.sku_id ? { ...l, qty: String(current + 1) } : l)));
      setNotice(t('inboundCreate.lineMerged', { sku: sku.code }));
      return;
    }
    setNotice(null);
    setLines((prev) => [...prev, { skuId: sku.sku_id, code: sku.code, name: sku.name, qty: '1' }]);
  }

  function setQty(skuId: string, qty: string): void {
    setError(null);
    setLines((prev) => prev.map((l) => (l.skuId === skuId ? { ...l, qty } : l)));
  }

  function removeLine(skuId: string): void {
    setError(null);
    setNotice(null);
    setLines((prev) => prev.filter((l) => l.skuId !== skuId));
  }

  async function submit(): Promise<void> {
    if (!ready || lines.length === 0 || hasProblems) return;
    setSubmitting(true);
    setError(null);
    try {
      setCreated(
        await endpoints.createInbound({
          idempotencyKey,
          ownerId,
          warehouseId,
          reference: reference.trim() || null,
          // the chosen calendar day, noon UTC: a date without a time zone must not slip to the neighbouring day
          expectedAt: expectedDate ? `${expectedDate}T12:00:00Z` : null,
          lines: lines.map((l) => ({ skuId: l.skuId, qty: l.qty.trim() })),
        }),
      );
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  function startAnother(): void {
    setCreated(null);
    setLines([]);
    setNotice(null);
    setReference('');
    setExpectedDate('');
    setSearch('');
    setIdempotencyKey(newIdempotencyKey());
    skus.reload();
  }

  if (options.status === 'loading') return <LoadingState />;
  if (options.status === 'error') return <ErrorBanner error={options.error} onRetry={options.reload} />;

  if (created) {
    return (
      <div>
        <div className="page-header">
          <h1>{t('inboundCreate.title')}</h1>
        </div>
        <div className="card" role="status">
          <p style={{ marginTop: 0 }}>
            <span className="badge ok">{t('inboundCreate.success', { number: created.number })}</span>
          </p>
          <div className="form-actions">
            <Link className="button-link" to={`/inbounds/${created.inboundShipmentId}`}>
              {t('inboundCreate.open', { number: created.number })}
            </Link>
            <button type="button" onClick={startAnother}>
              {t('inboundCreate.another')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const createError = error ? describeInboundError(t, error, lines) : null;

  return (
    <div className="order-create">
      <div className="page-header">
        <div>
          <h1>{t('inboundCreate.title')}</h1>
          <div className="sub">{t('inboundCreate.subtitle')}</div>
        </div>
      </div>

      <div className="card">
        <div className="form-grid">
          <div className="field-row">
            <label htmlFor="ic-seller">{t('inboundCreate.seller')}</label>
            <select
              id="ic-seller"
              value={ownerId}
              onChange={(e) => {
                setOwnerId(e.target.value);
                setWarehouseId('');
                setLines([]);
                setNotice(null);
                setError(null);
              }}
            >
              <option value="">{t('inboundCreate.selectSeller')}</option>
              {options.data.sellers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.code})
                </option>
              ))}
            </select>
          </div>
          <div className="field-row">
            <label htmlFor="ic-warehouse">{t('inboundCreate.warehouse')}</label>
            <select id="ic-warehouse" value={warehouseId} disabled={!ownerId || warehouses.status === 'loading' || offered.length === 0} onChange={(e) => { setWarehouseId(e.target.value); setError(null); }}>
              <option value="">{t('inboundCreate.selectWarehouse')}</option>
              {offered.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} ({w.code})
                </option>
              ))}
            </select>
            {ownerId && warehouses.status === 'success' && warehouses.data.reason !== 'READY' && (
              <span className="field-error" role="alert">{t(`inboundCreate.warehouseReason.${warehouses.data.reason}`)}</span>
            )}
            {ownerId && warehouses.status === 'error' && <ErrorBanner error={warehouses.error} onRetry={warehouses.reload} />}
          </div>
        </div>
        <p className="disabled-note" style={{ marginTop: 0 }}>{t('inboundCreate.sellerChangeResets')}</p>
      </div>

      <div className="card">
        {!ownerId ? (
          <p className="sub" style={{ margin: 0 }}>{t('inboundCreate.chooseSellerFirst')}</p>
        ) : (
          <>
            <div className="field-row">
              <label htmlFor="ic-search">{t('inboundCreate.findItem')}</label>
              <input id="ic-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
            </div>
            {skus.status === 'loading' && <LoadingState />}
            {skus.status === 'error' && <ErrorBanner error={skus.error} onRetry={skus.reload} />}
            {skus.status === 'success' &&
              (skus.data.rows.length === 0 ? (
                <p className="sub">{t('inboundCreate.noItemsFound')}</p>
              ) : (
                <ul className="sku-results" aria-label={t('inboundCreate.itemResults')}>
                  {skus.data.rows.map((s) => (
                    <li key={s.sku_id}>
                      <div>
                        <div>
                          <strong>{s.code}</strong> — {s.name}
                        </div>
                        <SkuMeta code={s.code} article={s.article} size={s.size} color={s.color} barcodes={s.barcodes} barcode={s.barcode} />
                      </div>
                      <button type="button" onClick={() => addLine(s)}>
                        {t('inboundCreate.addItem', { sku: s.code })}
                      </button>
                    </li>
                  ))}
                </ul>
              ))}
          </>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('inboundCreate.lines')}</h2>
        {notice && <div className="notice" role="status">{notice}</div>}
        {lines.length === 0 ? (
          <p className="sub" style={{ margin: 0 }}>{t('inboundCreate.noLines')}</p>
        ) : (
          <div className="table-scroll">
            <table className="lines-table">
              <thead>
                <tr>
                  <th>{t('inboundCreate.colItem')}</th>
                  <th>{t('inboundCreate.colQty')}</th>
                  <th>
                    <span className="sr-only">{t('common.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const problem = problems.get(l.skuId) ?? null;
                  const errId = `ic-qty-err-${l.skuId}`;
                  return (
                    <tr key={l.skuId}>
                      <td>
                        <strong>{l.code}</strong> — {l.name}
                      </td>
                      <td className="lines-qty">
                        <input
                          className="qty-input"
                          inputMode="numeric"
                          aria-label={t('inboundCreate.qtyFor', { sku: l.code })}
                          aria-invalid={problem ? 'true' : 'false'}
                          aria-describedby={problem ? errId : undefined}
                          value={l.qty}
                          onChange={(e) => setQty(l.skuId, e.target.value)}
                        />
                        {problem && (
                          <div className="field-error" id={errId}>
                            {problem}
                          </div>
                        )}
                      </td>
                      <td className="lines-remove">
                        <button type="button" className="danger" onClick={() => removeLine(l.skuId)} aria-label={t('inboundCreate.removeLine', { sku: l.code })}>
                          {t('inboundCreate.remove')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('inboundCreate.details')}</h2>
        <div className="form-grid">
          <div className="field-row">
            <label htmlFor="ic-reference">{t('inboundCreate.reference')}</label>
            <input id="ic-reference" value={reference} maxLength={40} onChange={(e) => setReference(e.target.value)} aria-describedby="ic-reference-hint" />
            <span className="disabled-note" id="ic-reference-hint">{t('inboundCreate.referenceHint')}</span>
          </div>
          <div className="field-row">
            <label htmlFor="ic-expected">{t('inboundCreate.expected')}</label>
            <input id="ic-expected" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        {createError ? (
          <div className="error-banner" role="alert">
            <div>{createError}</div>
            {error instanceof ApiError && <div className="rid">request_id: {error.requestId}</div>}
          </div>
        ) : (
          Boolean(error) && <ErrorBanner error={error} />
        )}
        {hasProblems && <p className="field-error" style={{ marginTop: 0 }}>{t('inboundCreate.fixErrors')}</p>}
        <div className="form-actions" style={{ marginTop: 0 }}>
          <button type="button" className="primary" disabled={submitting || !ready || lines.length === 0 || hasProblems} onClick={() => void submit()}>
            {submitting ? t('inboundCreate.submitting') : t('inboundCreate.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
